import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { PageHeader } from '../../../components/ui/PageHeader'
import { Spinner } from '../../../components/ui/Spinner'
import { Alert } from '../../../components/ui/Alert'
import { Modal } from '../../../components/ui/Modal'
import { selectClass } from '../../../components/ui/FormField'
import { api } from '../../../api/axios'
import { printComprovanteInscricaoThermal } from '../../../lib/printThermal'
import type { Inscricao } from '../../inscricoes/types'

interface MinhaInscricao extends Inscricao {
  numero_etapa: number
  forma_pagamento?: string | null
}

const STATUS_BADGE: Record<string, string> = {
  ativa: 'bg-green-100 text-green-700',
  cancelada: 'bg-red-100 text-red-700',
  finalizada: 'bg-compumaq-pretoSuave text-compumaq-textoSuave',
  confirmada: 'bg-compumaq-dourado/15 text-compumaq-doradoClaro',
  pendente_pagamento: 'bg-yellow-100 text-yellow-700',
}

function formatCurrency(val: number | string | null | undefined): string {
  if (val === null || val === undefined) return 'R$ —'
  const n = typeof val === 'string' ? parseFloat(val) : val
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(n)
}

function formaPagamentoLabel(forma?: string | null): string {
  if (!forma) return '—'
  const map: Record<string, string> = {
    pix: 'PIX',
    cartao: 'Cartao de Credito',
    pos: 'Cartao POS',
    isento: 'Isento',
  }
  return map[forma] ?? forma
}

export function MinhasInscricoesPage() {
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null)
  const [pagamentoInscricao, setPagamentoInscricao] = useState<MinhaInscricao | null>(null)
  const [pagamentoTodas, setPagamentoTodas] = useState(false)
  const [paymentMode, setPaymentMode] = useState<'none' | 'pix' | 'cartao'>('none')
  const [submitting, setSubmitting] = useState(false)
  const [cpfBusca, setCpfBusca] = useState('')
  const [buscarPorCpf, setBuscarPorCpf] = useState(false)

  // Vincular arma a inscricao
  const [vincularTarget, setVincularTarget] = useState<MinhaInscricao | null>(null)
  const [armaSelecionada, setArmaSelecionada] = useState<number | ''>('')
  const qc = useQueryClient()

  const { data: armasData } = useQuery({
    queryKey: ['minhas-armas'],
    queryFn: async () => {
      const r = await api.get<{ data: Array<{ idarma: number; marca: string; modelo: string; calibre: string; tipo: string }> }>('/publico/minhas-armas')
      return r.data.data
    },
  })
  const armas = armasData ?? []

  const vincularMut = useMutation({
    mutationFn: async (vars: { idinscricao: number; idarma: number | null }) =>
      api.patch(`/publico/minhas-inscricoes/${vars.idinscricao}/arma`, { idarma: vars.idarma }).then(r => r.data.data),
    onSuccess: () => {
      setAlert({ type: 'success', message: 'Arma vinculada com sucesso!' })
      setVincularTarget(null); setArmaSelecionada('')
      qc.invalidateQueries({ queryKey: ['minhas-inscricoes'] })
      qc.invalidateQueries({ queryKey: ['minhas-inscricoes-cpf'] })
    },
    onError: (err: any) => {
      setAlert({ type: 'error', message: err?.response?.data?.error || 'Erro ao vincular arma' })
    },
  })

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['minhas-inscricoes'],
    queryFn: async () => {
      const resp = await api.get<{ data: MinhaInscricao[] }>('/publico/minhas-inscricoes')
      return resp.data.data
    },
  })

  const { data: dataPorCpf, isLoading: loadingPorCpf, refetch: refetchPorCpf } = useQuery({
    queryKey: ['minhas-inscricoes-cpf', cpfBusca.replace(/\D/g, '')],
    queryFn: async () => {
      const resp = await api.get<{ data: MinhaInscricao[] }>(`/publico/minhas-inscricoes?cpf=${cpfBusca}`)
      return resp.data.data
    },
    enabled: buscarPorCpf && cpfBusca.replace(/\D/g, '').length === 11,
  })

  const inscricoes = buscarPorCpf ? (dataPorCpf ?? []) : (data ?? [])
  const carregando = buscarPorCpf ? loadingPorCpf : isLoading

  const inscricoesPendentes = inscricoes.filter(i => i.status === 'pendente_pagamento') ?? []
  const totalPendente = inscricoesPendentes.reduce((sum, i) => sum + (typeof i.valor_cobrado === 'string' ? parseFloat(i.valor_cobrado) : (i.valor_cobrado ?? 0)), 0)

  function imprimirComprovante(inscricoes: MinhaInscricao | MinhaInscricao[]) {
    const lista = Array.isArray(inscricoes) ? inscricoes : [inscricoes]
    if (lista.length === 0) return

    const total = lista.reduce((sum, i) => sum + (typeof i.valor_cobrado === 'string' ? parseFloat(i.valor_cobrado) : (i.valor_cobrado ?? 0)), 0)
    const primeira = lista[0]

    const itensHtml = lista.map(i => `
      <tr>
        <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;">${i.modalidade_nome ?? 'Inscricao'} — ${i.campeonato_descricao ?? ''} (Etapa ${i.numero_etapa ?? ''})</td>
        <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;text-align:right;">${formatCurrency(i.valor_cobrado)}</td>
      </tr>
    `).join('')

    const html = `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8"/>
  <title>Comprovante de Inscricao</title>
  <style>
    body { font-family: Arial, sans-serif; font-size: 13px; color: #111; margin: 32px; }
    h1 { font-size: 18px; margin-bottom: 4px; }
    .subtitle { color: #555; font-size: 12px; margin-bottom: 20px; }
    .info { margin-bottom: 16px; }
    .info-row { display: flex; gap: 16px; margin-bottom: 6px; }
    .label { font-weight: bold; min-width: 100px; }
    table { width: 100%; border-collapse: collapse; margin-top: 12px; }
    thead { background: #f3f4f6; }
    th { padding: 8px; text-align: left; font-size: 12px; border-bottom: 2px solid #d1d5db; }
    .total-row td { font-weight: bold; padding-top: 10px; font-size: 14px; }
    .footer { margin-top: 24px; font-size: 11px; color: #888; border-top: 1px solid #e5e7eb; padding-top: 10px; }
  </style>
</head>
<body>
  <h1>Comprovante de Inscricao</h1>
  <div class="subtitle">Campeonato de Tiro</div>
  <div class="info">
    <div class="info-row"><span class="label">Atirador:</span><span>${primeira.atirador_nome ?? ''}</span></div>
    <div class="info-row"><span class="label">CPF:</span><span>${primeira.atirador_cpf ?? ''}</span></div>
    <div class="info-row"><span class="label">Clube:</span><span>${primeira.clube_nome ?? ''}</span></div>
  </div>
  <table>
    <thead>
      <tr>
        <th>Item</th>
        <th style="text-align:right;">Valor</th>
      </tr>
    </thead>
    <tbody>
      ${itensHtml}
      <tr class="total-row">
        <td style="padding:10px 8px 0;">Total ${lista.length > 1 ? `(${lista.length} inscricoes)` : ''}</td>
        <td style="padding:10px 8px 0;text-align:right;">${formatCurrency(total)}</td>
      </tr>
    </tbody>
  </table>
  <div class="footer">Emitido em ${new Date().toLocaleString('pt-BR')}</div>
</body>
</html>`
    const win = window.open('', '_blank', 'width=700,height=600')
    if (win) {
      win.document.write(html)
      win.document.close()
      win.focus()
      setTimeout(() => win.print(), 400)
    }
  }

  function imprimirComprovanteThermal(inscricao: MinhaInscricao | MinhaInscricao[]) {
    const lista = Array.isArray(inscricao) ? inscricao : [inscricao]
    if (lista.length === 0) return
    const i = lista[0]
    printComprovanteInscricaoThermal({
      atirador_nome: i.atirador_nome ?? '',
      atirador_cpf: i.atirador_cpf ?? '',
      clube_nome: i.clube_nome ?? '',
      campeonato_descricao: i.campeonato_descricao ?? '',
      etapa_titulo: i.etapa_titulo ?? `Etapa ${String(i.numero_etapa) ?? ''}`,
      modalidade_nome: i.modalidade_nome ?? '',
      numero_inscricao: i.numero_inscricao,
      categoria: i.categoria,
      valor_cobrado: i.valor_cobrado,
      forma_pagamento: i.forma_pagamento,
      status: i.status,
    })
  }

  async function handlePagar(forma: 'pix' | 'cartao') {
    if (!pagamentoInscricao && !pagamentoTodas) return
    setSubmitting(true)
    try {
      if (forma === 'pix') {
        setAlert({ type: 'success', message: 'Para pagamento via PIX, utilize a chave PIX disponivel no local do campeonato ou entre em contato com a organizacao.' })
      } else {
        setAlert({ type: 'success', message: 'Para pagamento via cartao, acesse a pagina de inscricao publica ou entre em contato com a organizacao.' })
      }
      setPagamentoInscricao(null)
      setPagamentoTodas(false)
      setPaymentMode('none')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Minhas Inscricoes"
        description="Visualize e gerencie suas inscricoes nos campeonatos"
      />

      {alert && <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />}

      {/* Resumo de pendentes */}
      {inscricoesPendentes.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <p className="text-sm text-yellow-800 font-medium">
              {inscricoesPendentes.length} inscricao{inscricoesPendentes.length > 1 ? 's' : ''} com pagamento pendente
            </p>
            <p className="text-lg font-bold text-yellow-900">{formatCurrency(totalPendente)}</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => { setPagamentoTodas(true); setPaymentMode('none') }}
              className="px-4 py-2 bg-green-600 text-white text-sm font-semibold rounded-lg hover:bg-green-700"
            >
              Pagar Todos
            </button>
          </div>
        </div>
      )}

      {/* Botao reimprimir todas */}
      {inscricoes && inscricoes.length > 1 && (
        <div className="flex justify-end gap-2">
          <button
            onClick={() => imprimirComprovante(inscricoes)}
            className="px-4 py-2 border border-compumaq-dourado/25 text-compumaq-texto text-sm font-medium rounded-lg hover:bg-compumaq-preto flex items-center gap-2"
          >
            <span>🖨️</span> Reimprimir Todas
          </button>
          <button
            onClick={() => imprimirComprovanteThermal(inscricoes)}
            className="px-4 py-2 border border-compumaq-dourado/25 text-compumaq-texto text-sm font-medium rounded-lg hover:bg-compumaq-preto flex items-center gap-2"
            title="Imprimir na impressora Bluetooth (termica)"
          >
            <span>🖨️</span> Termica Todas
          </button>
        </div>
      )}

      <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 shadow-sm overflow-hidden">
        {carregando ? (
          <div className="flex justify-center py-16"><Spinner size="lg" /></div>
        ) : !inscricoes?.length ? (
          <div className="text-center py-16 text-compumaq-textoSuave">
            <p className="text-4xl mb-3">📋</p>
            <p className="font-medium">Nenhuma inscricao encontrada</p>
            <p className="text-sm text-compumaq-textoSuave mt-1">Nao encontramos inscricoes vinculadas ao seu usuario.</p>

            {/* Busca por CPF */}
            <div className="mt-6 max-w-sm mx-auto px-4">
              <p className="text-sm text-compumaq-textoSuave mb-2">Buscar pelo CPF:</p>
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="000.000.000-00"
                  value={cpfBusca}
                  onChange={e => {
                    const raw = e.target.value.replace(/\D/g, '')
                    let masked = raw
                    if (raw.length > 3) masked = raw.slice(0, 3) + '.' + raw.slice(3)
                    if (raw.length > 6) masked = raw.slice(0, 3) + '.' + raw.slice(3, 6) + '.' + raw.slice(6)
                    if (raw.length > 9) masked = raw.slice(0, 3) + '.' + raw.slice(3, 6) + '.' + raw.slice(6, 9) + '-' + raw.slice(9, 11)
                    setCpfBusca(masked)
                    setBuscarPorCpf(false)
                  }}
                  maxLength={14}
                  className="flex-1 px-3 py-2 border border-compumaq-dourado/25 rounded-lg text-sm focus:ring-2 focus:ring-compumaq-dourado focus:border-compumaq-dourado"
                />
                <button
                  onClick={() => { setBuscarPorCpf(true); refetchPorCpf() }}
                  disabled={cpfBusca.replace(/\D/g, '').length !== 11}
                  className="px-4 py-2 bg-compumaq-dourado text-compumaq-texto text-sm font-medium rounded-lg hover:bg-compumaq-doradoClaro disabled:opacity-50"
                >
                  Buscar
                </button>
              </div>
              {buscarPorCpf && !loadingPorCpf && dataPorCpf?.length === 0 && (
                <p className="text-xs text-red-600 mt-2">Nenhuma inscricao encontrada para este CPF.</p>
              )}
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-compumaq-preto border-b border-compumaq-dourado/25">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Campeonato</th>
                  <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Modalidade</th>
                  <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Etapa</th>
                  <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Cat.</th>
                  <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Insc #</th>
                  <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Valor</th>
                  <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Status</th>
                  <th className="text-right px-4 py-3 font-medium text-compumaq-textoSuave">Acoes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-compumaq-dourado/25">
                {inscricoes.map(i => (
                  <tr key={i.idinscricao} className="hover:bg-compumaq-preto">
                    <td className="px-4 py-3 text-compumaq-texto text-xs max-w-[140px] truncate">{i.campeonato_descricao ?? '—'}</td>
                    <td className="px-4 py-3 text-compumaq-texto">{i.modalidade_nome ?? '—'}</td>
                    <td className="px-4 py-3 text-compumaq-textoSuave text-xs">{i.etapa_titulo ?? '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${i.categoria === 'dama' ? 'bg-pink-100 text-pink-700' : 'bg-compumaq-dourado/15 text-compumaq-doradoClaro'}`}>
                        {i.categoria}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-compumaq-textoSuave text-center font-mono">#{i.numero_inscricao}</td>
                    <td className="px-4 py-3 text-compumaq-texto font-medium">{formatCurrency(i.valor_cobrado)}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_BADGE[i.status] ?? ''}`}>
                        {i.status === 'pendente_pagamento' ? 'Pendente' : i.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => imprimirComprovante(i)}
                          className="text-xs text-compumaq-doradoClaro hover:text-compumaq-doradoClaro font-medium whitespace-nowrap"
                        >
                          Reimprimir
                        </button>
                        <button
                          onClick={() => imprimirComprovanteThermal(i)}
                          className="text-xs text-compumaq-textoSuave hover:text-compumaq-texto font-medium whitespace-nowrap"
                          title="Imprimir na impressora Bluetooth (termica)"
                        >
                          Termica
                        </button>
                        {i.status === 'pendente_pagamento' && (
                          <button
                            onClick={() => { setPagamentoInscricao(i); setPagamentoTodas(false); setPaymentMode('none') }}
                            className="text-xs text-green-600 hover:text-green-800 font-medium whitespace-nowrap"
                          >
                            Pagar
                          </button>
                        )}
                        <button
                          onClick={() => { setVincularTarget(i); setArmaSelecionada(i.idarma ?? '') }}
                          className="text-xs text-amber-600 hover:text-amber-800 font-medium whitespace-nowrap"
                          title="Vincular arma usada nesta inscricao"
                        >
                          {i.idarma ? 'Trocar Arma' : 'Vincular Arma'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de Pagamento Individual */}
      {pagamentoInscricao && !pagamentoTodas && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-compumaq-pretoSuave rounded-xl shadow-xl w-full max-w-md p-6 space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-bold text-compumaq-texto">Pagamento da Inscricao</h3>
              <button
                onClick={() => { setPagamentoInscricao(null); setPaymentMode('none') }}
                className="text-compumaq-textoSuave hover:text-compumaq-textoSuave"
              >
                ✕
              </button>
            </div>

            <div className="bg-compumaq-preto rounded-lg p-3">
              <p className="text-xs text-compumaq-textoSuave">Inscricao</p>
              <p className="font-semibold text-compumaq-texto">{pagamentoInscricao.modalidade_nome}</p>
              <p className="text-xs text-compumaq-textoSuave">{pagamentoInscricao.campeonato_descricao} — Etapa {pagamentoInscricao.numero_etapa}</p>
              <p className="text-sm font-bold text-green-700 mt-1">{formatCurrency(pagamentoInscricao.valor_cobrado)}</p>
            </div>

            {paymentMode === 'none' && (
              <div className="space-y-3">
                <p className="text-sm text-compumaq-textoSuave">Escolha a forma de pagamento:</p>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setPaymentMode('pix')}
                    className="flex flex-col items-center gap-2 p-4 rounded-xl border border-compumaq-dourado/25 bg-compumaq-preto hover:bg-green-50 hover:border-green-400 transition-all"
                  >
                    <span className="text-3xl">⚡</span>
                    <span className="text-compumaq-texto font-semibold text-sm">PIX</span>
                    <span className="text-compumaq-textoSuave text-xs">Pagamento instantaneo</span>
                  </button>
                  <button
                    onClick={() => setPaymentMode('cartao')}
                    className="flex flex-col items-center gap-2 p-4 rounded-xl border border-compumaq-dourado/25 bg-compumaq-preto hover:bg-compumaq-dourado/10 hover:border-compumaq-dourado/40 transition-all"
                  >
                    <span className="text-3xl">💳</span>
                    <span className="text-compumaq-texto font-semibold text-sm">Cartao</span>
                    <span className="text-compumaq-textoSuave text-xs">Credito/Debito</span>
                  </button>
                </div>
              </div>
            )}

            {paymentMode === 'pix' && (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <p className="text-2xl mb-2">⚡</p>
                  <p className="font-semibold text-compumaq-texto">Pagamento via PIX</p>
                  <p className="text-sm text-compumaq-textoSuave mt-1">Entre em contato com a organizacao do campeonato para realizar o pagamento via PIX.</p>
                  <p className="text-2xl font-bold text-compumaq-texto mt-3">{formatCurrency(pagamentoInscricao.valor_cobrado)}</p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setPaymentMode('none')}
                    className="flex-none px-4 py-2.5 border border-compumaq-dourado/25 text-sm rounded-lg hover:bg-compumaq-preto"
                  >
                    Voltar
                  </button>
                  <button
                    onClick={() => handlePagar('pix')}
                    disabled={submitting}
                    className="flex-1 py-2.5 bg-green-600 text-white text-sm font-semibold rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    {submitting ? 'Processando...' : 'Confirmar (PIX)'}
                  </button>
                </div>
              </div>
            )}

            {paymentMode === 'cartao' && (
              <div className="space-y-4">
                <div className="bg-compumaq-dourado/10 border border-compumaq-dourado/25 rounded-lg p-4 text-center">
                  <p className="text-2xl mb-2">💳</p>
                  <p className="font-semibold text-compumaq-texto">Pagamento via Cartao</p>
                  <p className="text-sm text-compumaq-textoSuave mt-1">Entre em contato com a organizacao do campeonato para realizar o pagamento via cartao.</p>
                  <p className="text-2xl font-bold text-compumaq-texto mt-3">{formatCurrency(pagamentoInscricao.valor_cobrado)}</p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setPaymentMode('none')}
                    className="flex-none px-4 py-2.5 border border-compumaq-dourado/25 text-sm rounded-lg hover:bg-compumaq-preto"
                  >
                    Voltar
                  </button>
                  <button
                    onClick={() => handlePagar('cartao')}
                    disabled={submitting}
                    className="flex-1 py-2.5 bg-compumaq-dourado text-compumaq-texto text-sm font-semibold rounded-lg hover:bg-compumaq-doradoClaro disabled:opacity-50"
                  >
                    {submitting ? 'Processando...' : 'Confirmar (Cartao)'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal de Pagamento de Todas */}
      {pagamentoTodas && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-compumaq-pretoSuave rounded-xl shadow-xl w-full max-w-md p-6 space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-bold text-compumaq-texto">Pagamento de Todas as Inscricoes</h3>
              <button
                onClick={() => { setPagamentoTodas(false); setPaymentMode('none') }}
                className="text-compumaq-textoSuave hover:text-compumaq-textoSuave"
              >
                ✕
              </button>
            </div>

            <div className="bg-compumaq-preto rounded-lg p-3 max-h-48 overflow-y-auto">
              <p className="text-xs text-compumaq-textoSuave mb-2">Inscricoes pendentes:</p>
              {inscricoesPendentes.map(i => (
                <div key={i.idinscricao} className="flex justify-between text-sm py-1 border-b border-compumaq-dourado/25 last:border-0">
                  <span className="text-compumaq-texto">{i.modalidade_nome} — Etapa {i.numero_etapa}</span>
                  <span className="font-medium text-compumaq-texto">{formatCurrency(i.valor_cobrado)}</span>
                </div>
              ))}
              <div className="flex justify-between text-sm font-bold text-compumaq-texto pt-2 mt-1 border-t border-compumaq-dourado/25">
                <span>Total ({inscricoesPendentes.length} inscricoes)</span>
                <span className="text-green-700">{formatCurrency(totalPendente)}</span>
              </div>
            </div>

            {paymentMode === 'none' && (
              <div className="space-y-3">
                <p className="text-sm text-compumaq-textoSuave">Escolha a forma de pagamento:</p>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setPaymentMode('pix')}
                    className="flex flex-col items-center gap-2 p-4 rounded-xl border border-compumaq-dourado/25 bg-compumaq-preto hover:bg-green-50 hover:border-green-400 transition-all"
                  >
                    <span className="text-3xl">⚡</span>
                    <span className="text-compumaq-texto font-semibold text-sm">PIX</span>
                    <span className="text-compumaq-textoSuave text-xs">Pagamento instantaneo</span>
                  </button>
                  <button
                    onClick={() => setPaymentMode('cartao')}
                    className="flex flex-col items-center gap-2 p-4 rounded-xl border border-compumaq-dourado/25 bg-compumaq-preto hover:bg-compumaq-dourado/10 hover:border-compumaq-dourado/40 transition-all"
                  >
                    <span className="text-3xl">💳</span>
                    <span className="text-compumaq-texto font-semibold text-sm">Cartao</span>
                    <span className="text-compumaq-textoSuave text-xs">Credito/Debito</span>
                  </button>
                </div>
              </div>
            )}

            {paymentMode === 'pix' && (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <p className="text-2xl mb-2">⚡</p>
                  <p className="font-semibold text-compumaq-texto">Pagamento via PIX</p>
                  <p className="text-sm text-compumaq-textoSuave mt-1">Entre em contato com a organizacao do campeonato para realizar o pagamento via PIX de todas as inscricoes.</p>
                  <p className="text-2xl font-bold text-compumaq-texto mt-3">{formatCurrency(totalPendente)}</p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setPaymentMode('none')}
                    className="flex-none px-4 py-2.5 border border-compumaq-dourado/25 text-sm rounded-lg hover:bg-compumaq-preto"
                  >
                    Voltar
                  </button>
                  <button
                    onClick={() => handlePagar('pix')}
                    disabled={submitting}
                    className="flex-1 py-2.5 bg-green-600 text-white text-sm font-semibold rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    {submitting ? 'Processando...' : 'Confirmar Pagamento (PIX)'}
                  </button>
                </div>
              </div>
            )}

            {paymentMode === 'cartao' && (
              <div className="space-y-4">
                <div className="bg-compumaq-dourado/10 border border-compumaq-dourado/25 rounded-lg p-4 text-center">
                  <p className="text-2xl mb-2">💳</p>
                  <p className="font-semibold text-compumaq-texto">Pagamento via Cartao</p>
                  <p className="text-sm text-compumaq-textoSuave mt-1">Entre em contato com a organizacao do campeonato para realizar o pagamento via cartao de todas as inscricoes.</p>
                  <p className="text-2xl font-bold text-compumaq-texto mt-3">{formatCurrency(totalPendente)}</p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setPaymentMode('none')}
                    className="flex-none px-4 py-2.5 border border-compumaq-dourado/25 text-sm rounded-lg hover:bg-compumaq-preto"
                  >
                    Voltar
                  </button>
                  <button
                    onClick={() => handlePagar('cartao')}
                    disabled={submitting}
                    className="flex-1 py-2.5 bg-compumaq-dourado text-compumaq-texto text-sm font-semibold rounded-lg hover:bg-compumaq-doradoClaro disabled:opacity-50"
                  >
                    {submitting ? 'Processando...' : 'Confirmar Pagamento (Cartao)'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal: Vincular Arma a inscricao */}
      <Modal
        open={!!vincularTarget}
        onClose={() => { setVincularTarget(null); setArmaSelecionada('') }}
        title="Vincular Arma a Inscricao"
        size="sm"
      >
        {vincularTarget && (
          <div className="space-y-4">
            <div className="bg-compumaq-preto rounded-lg p-3">
              <p className="text-xs text-compumaq-textoSuave">Inscricao</p>
              <p className="font-semibold text-compumaq-texto text-sm">
                {vincularTarget.modalidade_nome} — {vincularTarget.etapa_titulo}
              </p>
              {vincularTarget.idarma && (
                <p className="text-xs text-amber-700 mt-1">Arma atual: {vincularTarget.arma_descricao || '—'}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-compumaq-texto mb-1">Arma</label>
              <select
                className={selectClass}
                value={armaSelecionada}
                onChange={e => setArmaSelecionada(e.target.value ? parseInt(e.target.value) : '')}
              >
                <option value="">— sem arma —</option>
                {armas.map(a => (
                  <option key={a.idarma} value={a.idarma}>
                    {a.marca} {a.modelo} ({a.calibre}) — {a.tipo}
                  </option>
                ))}
              </select>
              {armas.length === 0 && (
                <p className="text-xs text-amber-600 mt-2">
                  Voce ainda nao cadastrou armas. Acesse "Minhas Armas" no menu para cadastrar.
                </p>
              )}
            </div>

            <div className="flex gap-3 pt-1">
              <button
                type="button"
                onClick={() => { setVincularTarget(null); setArmaSelecionada('') }}
                className="flex-1 py-2.5 border border-compumaq-dourado/25 text-sm rounded-lg hover:bg-compumaq-preto"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => vincularMut.mutate({
                  idinscricao: vincularTarget.idinscricao,
                  idarma: armaSelecionada === '' ? null : (armaSelecionada as number),
                })}
                disabled={vincularMut.isPending}
                className="flex-1 py-2.5 bg-amber-600 text-white text-sm font-semibold rounded-lg hover:bg-amber-700 disabled:opacity-50"
              >
                {vincularMut.isPending ? 'Salvando...' : 'Confirmar'}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}
